import { ApolloClient, makeVar } from '@apollo/client';
import posthog from 'posthog-js';
import {
  UserDocument,
  UserFragmentFragment,
  UserQuery,
  UserQueryVariables,
} from 'src/generated/graphql';

const SESSION_INVALIDATED_STORAGE_KEY = 'session-invalidated-by-another-device';

function readInitialSessionInvalidatedState() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.sessionStorage.getItem(SESSION_INVALIDATED_STORAGE_KEY) === 'true';
}

export const sessionInvalidatedVar = makeVar<boolean>(readInitialSessionInvalidatedState());

// The user the server has confirmed as signed in on this page, for session replay. It lives outside
// the Apollo cache because clearing the store drops the cache watches that would report a later
// sign-in. It is not persisted: a user restored from the cache may belong to an ended session.
export const confirmedUserVar = makeVar<UserFragmentFragment | undefined>(undefined);
// Changes on every confirmation and revocation, so a startup answer that arrives late is ignored.
let confirmationVersion = 0;

// A response may lack the user (the e2e mocks omit it), and that must not break the sign-in.
export function confirmSessionUser(user: UserFragmentFragment | null | undefined) {
  if (!user?.isAuthenticated) return;
  confirmationVersion += 1;
  confirmedUserVar(user);
}

export function revokeSessionUser() {
  confirmationVersion += 1;
  confirmedUserVar(undefined);
}

// The user query requires a session, so an answer names the signed-in user. It bypasses the cache so
// a late answer cannot overwrite a newer sign-in there either. Failures are left to the Apollo error
// link, and the replay stays anonymous.
export async function confirmSessionFromServer(client: ApolloClient<object>) {
  const version = confirmationVersion;
  try {
    const { data } = await client.query<UserQuery, UserQueryVariables>({
      query: UserDocument,
      fetchPolicy: 'no-cache',
    });
    if (version === confirmationVersion) confirmSessionUser(data.user);
  } catch {
    // No confirmation.
  }
}

export function markSessionInvalidated() {
  revokeSessionUser();
  sessionInvalidatedVar(true);
  // The redirect to /login can unload the page before the cache is cleared, so the session
  // replay identity is reset here rather than when the user disappears from the cache.
  posthog.reset();

  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(SESSION_INVALIDATED_STORAGE_KEY, 'true');
  }
}

export function clearSessionInvalidated() {
  sessionInvalidatedVar(false);

  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(SESSION_INVALIDATED_STORAGE_KEY);
  }
}
